import { Controller } from '@hotwired/stimulus'
import { LocalClient } from '../../clients/local_client'

type PersistedFile = {
  id: string
  filename: string
  url: string
  content_type: string
}

export class FileUploadController extends Controller {
  static targets = [
    'fileInput',
    'previews',
    'hiddenInputs',
    'label',
    'field',
    'wrapper',
  ]
  static values = {
    fieldName: String,
    removeIcon: String,
    removeIconAlt: String,
    fileIcon: String,
    fileIconAlt: String,
    formObjectName: String,
    persistedFile: Object,
    acceptedTypes: Array,
  }
  static classes = ['hidden', 'show']

  declare readonly fileInputTarget: HTMLInputElement
  declare readonly previewsTarget: HTMLElement
  declare readonly hiddenInputsTarget: HTMLElement
  declare readonly labelTarget: HTMLElement
  declare readonly fieldTarget: HTMLElement
  declare readonly wrapperTarget: HTMLElement
  declare readonly fieldNameValue: string
  declare readonly removeIconValue: string
  declare readonly removeIconAltValue: string
  declare readonly fileIconValue: string
  declare readonly fileIconAltValue: string
  declare readonly formObjectNameValue: string
  declare readonly persistedFileValue: PersistedFile
  declare readonly acceptedTypesValue: string[]
  declare readonly hasLabelTarget: boolean
  declare readonly hasFieldTarget: boolean

  handleFileSelected(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (!file) return

    if (
      this.acceptedTypesValue.length > 0 &&
      !this.acceptedTypesValue.includes(file.type)
    ) {
      input.value = ''
      return
    }

    this.previewsTarget.innerHTML = ''

    const preview = this.createPreviewElement(file)
    this.previewsTarget.appendChild(preview)

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = file.name
    }
  }

  handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (!file) {
      this.hiddenInputsTarget.innerHTML = ''
    }
  }

  async removeFile(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const fileId = button.dataset.fileUploadIdParam

    const pathParts = window.location.pathname.split('/')
    const invoiceId = pathParts[2] // /invoices/:id/edit

    if (window.location.pathname.includes('/invoices/')) {
      await LocalClient.removeAttachedPdf(invoiceId)
    }

    if (fileId) {
      const input = this.hiddenInputsTarget.querySelector(
        `input[value="${fileId}"]`,
      )
      if (input) input.remove()
    }

    this.previewsTarget.innerHTML = ''
    this.fileInputTarget.value = ''

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = 'Click to edit name'
    }
  }

  showEdit() {
    if (!this.hasFieldTarget) return

    this.wrapperTarget.classList.add('hidden')
    this.fieldTarget.classList.remove('hidden')
    const input = this.fieldTarget.querySelector('input')
    if (input) {
      input.focus()
    }
  }

  hideEdit() {
    if (!this.hasFieldTarget) return

    this.wrapperTarget.classList.remove('hidden')
    this.fieldTarget.classList.add('hidden')
    const input = this.fieldTarget.querySelector('input') as HTMLInputElement
    if (input && input.value) {
      this.labelTarget.textContent = input.value
    }
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.hideEdit()
    }
  }

  triggerInput() {
    this.fileInputTarget.click()
  }

  private createPreviewElement(file: File): HTMLLIElement {
    const li = document.createElement('li')
    li.className = 'file-preview-item'

    const iconSpan = document.createElement('span')
    iconSpan.className = 'file-preview-icon'
    const img = document.createElement('img')
    img.src = this.fileIconValue
    img.alt = this.fileIconAltValue
    img.className = 'file-preview-image'
    iconSpan.appendChild(img)

    const filenameSpan = document.createElement('span')
    filenameSpan.className = 'file-preview-file-name'
    filenameSpan.textContent = file.name

    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'file-preview-remove'
    removeButton.dataset.action = 'click->file-upload#removeFile'
    const removeImg = document.createElement('img')
    removeImg.src = this.removeIconValue
    removeImg.alt = this.removeIconAltValue
    removeButton.appendChild(removeImg)

    li.appendChild(iconSpan)
    li.appendChild(filenameSpan)
    li.appendChild(removeButton)

    return li
  }
}
